import type { CSSProperties, ReactNode } from 'react'

import Image from 'next/image'
import { useState } from 'react'
import { cn } from '@/lib/utils'

type AssetFit = 'cover' | 'contain' | 'fill' | 'none'

interface AssetProps {
    src: string
    alt: string
    width: number
    height: number
    fit?: AssetFit
    sizes?: string
    quality?: number
    fallback?: ReactNode
    className?: string
    mediaClassName?: string
}

const ASSET_FIT: Record<AssetFit, string> = {
    cover: 'object-cover',
    contain: 'object-contain',
    fill: 'object-fill',
    none: 'object-none'
}

function isRawSource(src: string): boolean {
    if (src.startsWith('data:') || src.startsWith('blob:')) return true
    if (/^https?:\/\//i.test(src)) return true

    return src.split('?')[0].toLowerCase().endsWith('.svg')
}

export default function Asset({
    src,
    alt,
    width,
    height,
    fit = 'cover',
    sizes = '100vw',
    quality = 90,
    fallback,
    className,
    mediaClassName
}: AssetProps) {
    const [failedSrc, setFailedSrc] = useState<string | null>(null)

    if (failedSrc === src && fallback !== undefined) return <>{fallback}</>

    return (
        <span
            style={{ '--asset-ratio': `${width} / ${height}` } as CSSProperties}
            className={cn('relative block aspect-(--asset-ratio) w-full overflow-hidden', className)}
        >
            <Image
                src={src}
                alt={alt}
                fill
                sizes={sizes}
                quality={quality}
                unoptimized={isRawSource(src)}
                onError={() => setFailedSrc(src)}
                className={cn('absolute inset-0 size-full', ASSET_FIT[fit], mediaClassName)}
            />
        </span>
    )
}
